import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Building2, Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function HospitalLogin({ onLogin }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hospital, setHospital] = useState(null);
  const [branding, setBranding] = useState(null);
  const [error, setError] = useState('');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchHospitalAndBranding();
    } else {
      setLoading(false);
    }
  }, [slug]);

  // Update favicon dynamically
  useEffect(() => {
    if (branding?.favicon_url) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = branding.favicon_url;
      document.getElementsByTagName('head')[0].appendChild(link);
      
      // Cleanup - restore default favicon on unmount
      return () => {
        link.href = '/favicon.ico';
      };
    }
  }, [branding?.favicon_url]);

  const fetchHospitalAndBranding = async () => {
    try {
      // Fetch hospital info
      const hospitalResponse = await api.get(`/hospital/lookup/slug/${slug}`);
      setHospital(hospitalResponse.data);
      
      // Fetch public branding
      try {
        const brandingResponse = await api.get(`/branding/public/${slug}`);
        setBranding(brandingResponse.data);
      } catch (brandingError) {
        console.log('Branding not available, using defaults');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Hospital not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/hospital/${slug}/login`, { email, password });
      const { access_token, user, hospital: hospitalInfo } = response.data;
      
      // Store credentials
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      if (hospitalInfo) {
        localStorage.setItem('hospital', JSON.stringify(hospitalInfo));
      }
      
      toast.success(`Welcome to ${hospitalInfo?.name || 'your dashboard'}!`);
      
      // Call parent login handler if provided
      if (onLogin) {
        onLogin(user, access_token, hospitalInfo);
      }
      
      // Navigate to dashboard
      navigate('/');
      window.location.reload();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/reset-password?type=hospital', { state: { email, hospitalSlug: slug } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#4A238F] to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error && !hospital) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#4A238F] to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Hospital Not Found</h2>
              <p className="text-white/70 mb-6">{error}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-white text-[#4A238F] hover:bg-white/90"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go to Main Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Get branding settings with fallbacks
  const primaryColor = branding?.primary_color || hospital?.branding?.primary_color || '#4A238F';
  const secondaryColor = branding?.secondary_color || hospital?.branding?.secondary_color || '#7C3AED';
  const logoUrl = branding?.logo_url || hospital?.branding?.logo_url;
  const loginBackgroundUrl = branding?.login_background_url;
  const loginTitle = branding?.login_title || hospital?.name || 'Hospital Login';
  const loginSubtitle = branding?.login_subtitle || branding?.tagline || hospital?.branding?.tagline;
  const showPoweredBy = branding?.show_powered_by !== false; // Default to true

  // Background style - use custom image or gradient
  const backgroundStyle = loginBackgroundUrl
    ? {
        backgroundImage: `url(${loginBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {
        background: `linear-gradient(135deg, #1e293b 0%, ${primaryColor} 50%, #1e293b 100%)`
      };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={backgroundStyle}
    >
      {/* Overlay for background images to ensure text readability */}
      {loginBackgroundUrl && (
        <div className="absolute inset-0 bg-black/40" />
      )}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Hospital Branding */}
        <div className="text-center mb-6">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={hospital?.name || 'Hospital'} 
              className="h-20 max-w-[200px] mx-auto mb-4 rounded-xl bg-white p-2 object-contain"
            />
          ) : (
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ backgroundColor: primaryColor }}
            >
              <Building2 className="h-8 w-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{loginTitle}</h1>
          {loginSubtitle && (
            <p className="text-white/70 text-sm mt-1">{loginSubtitle}</p>
          )}
        </div>

        <Card className="bg-white/10 backdrop-blur-md border-white/20 shadow-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-center">Staff Login</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white/80">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white border-white/30 text-slate-900 placeholder:text-slate-400 pl-10 focus:border-white/50 focus:ring-white/20"
                    placeholder="your.email@hospital.com"
                    required
                    data-testid="hospital-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white/80">Password</Label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-xs text-white/60 hover:text-white transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50 z-10" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white border-white/30 text-slate-900 placeholder:text-slate-400 pl-10 pr-10 focus:border-white/50 focus:ring-white/20"
                    placeholder="Enter your password"
                    required
                    data-testid="hospital-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 z-10 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="password-toggle-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-3 text-white font-medium transition-all hover:opacity-90"
                style={{ 
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` 
                }}
                data-testid="hospital-login-btn"
              >
                {submitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-white/40 text-xs">
                This is a secure login for {hospital?.name || 'hospital'} staff only.
              </p>
              <Link 
                to="/login" 
                className="text-xs text-white/60 hover:text-white mt-2 inline-block transition-colors"
              >
                Not your hospital? Use main login
              </Link>
            </div>
          </CardContent>
        </Card>

        {showPoweredBy && (
          <p className="text-center text-white/30 text-xs mt-6">
            Powered by Zesta Healthcare Platform
          </p>
        )}
      </motion.div>
    </div>
  );
}
